import { type MouseEvent, useRef } from "react";

import { sourcesApiSlice } from "api/sourcesApi.slice";
import { HiddenImageUpload } from "common/components";
import { ApiErrorMessage } from "common/components/ApiErrorMessage/ApiErrorMessage";
import { logger } from "common/utilities/logger";
import { useTranslate } from "common/utilities/useTranslate";
import placeholderImageUrl from "domains/about/images/placeholder_image5.png";
import type { LocalFile } from "domains/createMapDetail/types";
import * as Styled from "domains/moderate/sources/components/SourceLogoImagePicker/SourceLogoImagePicker.styled";

type SourceLogoImagePickerProps = {
  accessibilityId?: string;
  imageUrl?: string;
  onSetImageUrl?: (url: string) => void;
};

export const SourceLogoImagePicker = ({ accessibilityId, imageUrl, onSetImageUrl }: SourceLogoImagePickerProps) => {
  const translate = useTranslate();

  const [uploadImageToS3, { error }] = sourcesApiSlice.endpoints.uploadSourceImage.useMutation();

  const hiddenImageUploadRef = useRef<HTMLInputElement>(null);

  const openHiddenImageUpload = (event: MouseEvent) => {
    event.preventDefault();
    hiddenImageUploadRef.current?.click();
  };

  const getS3UrlAndSetImage = async (options: LocalFile) => {
    const result = await uploadImageToS3({ file: options.file });

    if (result.error) {
      logger.error("[ImagePicker] Error uploading image to S3", result.error);
      return;
    }

    onSetImageUrl?.(result.data.favicon);
  };

  return (
    <div>
      <Styled.Container error={false}>
        <HiddenImageUpload accept="image/*" ref={hiddenImageUploadRef} onSetImage={getS3UrlAndSetImage} />
        <Styled.Image id={accessibilityId} src={imageUrl ?? placeholderImageUrl} />
        <Styled.Button onClick={openHiddenImageUpload} variant={imageUrl === undefined ? "filled" : "outlined"}>
          {translate(imageUrl === undefined ? "app.create.collection.add_image" : "app.create.collection.change_image")}
        </Styled.Button>
      </Styled.Container>
      <ApiErrorMessage error={error} />
    </div>
  );
};
